import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useTheSeaContext, LOGIN_GATE_AT } from "./TheSeaContext";
import api from "../../utils/api";
import { buildConversationDocx } from "../../utils/conversationDocx";

const AGENCY_CHIPS = ["ICH", "FDA", "EMA", "PMDA", "MHRA", "WHO"];

const MODALITY_CHIPS = ["Small molecule", "Biologics", "rAAV", "Cell therapy", "RNA LNP", "Other modalities"];

const ROUTE_CHIPS = ["Solid oral dosage form", "Intravenous", "Subcutaneous", "Other route of administration"];

const PHASE_CHIPS = ["Early phase", "Late phase", "Post approval"];

function LoginGateForm({ onCreateAccount }) {
  return (
    <>
      <p className="gate-title">You've asked {LOGIN_GATE_AT} questions 🎉</p>
      <p>Create a free account to keep the conversation going.</p>
      <button type="button" className="gate-btn" onClick={onCreateAccount}>
        Create free account
      </button>
    </>
  );
}

function PaywallGateForm({ onBuyPoints }) {
  return (
    <>
      <p className="gate-title">You're out of points</p>
      <p>Buy more points to keep asking questions.</p>
      <button type="button" className="gate-btn" onClick={onBuyPoints}>Buy points</button>
    </>
  );
}

function Message({ message, onCreateAccount, onBuyPoints }) {
  if (message.role === "user") {
    return <div className="msg msg-user"><p>{message.text}</p></div>;
  }

  if (message.role === "agent") {
    return (
      <div className="msg msg-agent">
        <div className="msg-markdown">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.text}</ReactMarkdown>
        </div>
        {message.sources && message.sources.length > 0 && (
          <div className="sources">
            {message.sources.map((s) => <span className="source-chip" key={s}>{s}</span>)}
          </div>
        )}
      </div>
    );
  }

  if (message.role === "gate-login") {
    return (
      <div className="msg msg-agent gate-card">
        <LoginGateForm onCreateAccount={onCreateAccount} />
      </div>
    );
  }

  if (message.role === "gate-paywall") {
    return (
      <div className="msg msg-agent gate-card">
        <PaywallGateForm onBuyPoints={onBuyPoints} />
      </div>
    );
  }

  return null;
}

export default function ChatAgent() {
  // All chat state (messages, typing indicator, lock state, input box, selected chips) used to
  // be local `useState` right here in ChatAgent. That broke on navigation: this component is
  // only rendered by TheSeaHome, and React Router unmounts TheSeaHome whenever you leave the
  // "/" route — unmounting throws away local state. Visiting About and coming back to Home
  // created a brand new ChatAgent with empty state every time.
  //
  // Fix: the state now lives in TheSeaContext (see TheSeaContext.js), which is provided by a
  // <TheSeaProvider> that wraps *all* of these routes as a group in App.js, not just Home. That
  // provider doesn't get unmounted when you navigate between its child routes, so the state
  // (and thus the conversation) survives. Below, we just rename the context's `chatX` fields
  // back to the plain local names (`messages`, `isTyping`, ...) so the rest of this file, and
  // the JSX below, didn't need to change at all.
  const {
    isLoggedIn,
    setQuestionCount,
    setPointsBalance,
    logConversation,
    cookiesAccepted,
    chatMessages: messages,
    addChatMessage: addMessage,
    chatIsTyping: isTyping,
    setChatIsTyping: setIsTyping,
    chatLocked: locked,
    setChatLocked: setLocked,
    chatLockedPlaceholder: lockedPlaceholder,
    setChatLockedPlaceholder: setLockedPlaceholder,
    chatInputValue: inputValue,
    setChatInputValue: setInputValue,
    chatSelectedChips: selectedChips,
    setChatSelectedChips: setSelectedChips,
    resetChat, // clears all of the above; used by "New chat"
  } = useTheSeaContext();

  const navigate = useNavigate();
  const location = useLocation();
  const hasRealAccount = isLoggedIn;

  const messagesRef = useRef(null);

  useEffect(() => {
    if (messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const handleQuestion = async (question) => {
    if (!question || !question.trim() || locked || !cookiesAccepted) return;

    addMessage({ role: "user", text: question });
    setIsTyping(true);

    try {
      const { data } = await api.post(
        "/anonymous/copilot/ask",
        { question },
        { timeout: 210000 } // Direct Line polling can take up to 3 min (backend directLinePollTimeout) + buffer
      );

      setIsTyping(false);
      setQuestionCount(data.questionCount);
      if (hasRealAccount) setPointsBalance(data.pointsBalance);

      if (data.gate === "login") {
        addMessage({ role: "gate-login" });
        setLocked(true);
        setLockedPlaceholder("Create a free account to continue…");
        return;
      }

      if (data.gate === "paywall") {
        addMessage({ role: "gate-paywall" });
        setLocked(true);
        setLockedPlaceholder("Buy points to continue…");
        return;
      }

      addMessage({ role: "agent", text: data.replyText });
      logConversation(question);
    } catch (err) {
      setIsTyping(false);
      addMessage({ role: "agent", text: "Sorry, the agent is temporarily unavailable. Please try again." });
    }
  };

  const handleNewChat = async () => {
    if (isTyping) return; // avoid racing an in-flight ask against the reset

    try {
      await api.post("/anonymous/copilot/reset");
    } catch (err) {
      // Best-effort: even if the backend call fails, clearing local state below still lets
      // the user start typing again — worst case the next question reuses the old Direct
      // Line conversation server-side, which isn't harmful, just not a fresh start.
    }

    resetChat(); // clears messages/typing/locked/input/chips in TheSeaContext, see there for details
  };

  const goToSignup = () => {
    navigate("/loginsignup", { state: { returnTo: location.pathname } });
  };

  const goToBuyPoints = () => {
    navigate("/points");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const value = inputValue;
    setInputValue("");
    setSelectedChips([]);
    handleQuestion(value);
  };

  const toggleChip = (value) => {
    setSelectedChips((prev) => {
      const isSelected = prev.includes(value);
      if (isSelected) {
        setInputValue((current) =>
          current
            .split(", ")
            .filter((part) => part !== value)
            .join(", ")
        );
        return prev.filter((v) => v !== value);
      }

      setInputValue((current) => (current ? `${current}, ${value}` : value));
      return [...prev, value];
    });
  };

  const hasConversation = messages.some((m) => m.role === "user");

  const generateReport = async () => {
    const blob = await buildConversationDocx(messages);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "the-sea-conversation-report.docx";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="agent-panel-wrap">
      <div className="agent-panel" id="agentPanel">
        <div className="agent-header">
          <span className="status-dot"></span>
          <span className="agent-title">askTheSea — Agent</span>
        </div>
        <p className="agent-intro">Hello, I'm here to support your CMC regulatory questions. To help me provide the most relevant response, please include: Health authority agency, Clinical development phase, Product modality, Dosage form, and Route of administration. The more context you provide, the more tailored my response will be.</p>
        {!locked && cookiesAccepted && (
          <div className="chat-suggestions" id="agencyChips">
            <span className="chip-row-label">Health authority or filing region:</span>
            {AGENCY_CHIPS.map((agency) => (
              <button
                key={agency}
                type="button"
                className={"chip" + (selectedChips.includes(agency) ? " chip-active" : "")}
                onClick={() => toggleChip(agency)}
              >
                {agency}
              </button>
            ))}
          </div>
        )}
        {!locked && cookiesAccepted && (
          <div className="chat-suggestions" id="modalityChips">
            <span className="chip-row-label">Product modality:</span>
            {MODALITY_CHIPS.map((modality) => (
              <button
                key={modality}
                type="button"
                className={"chip" + (selectedChips.includes(modality) ? " chip-active" : "")}
                onClick={() => toggleChip(modality)}
              >
                {modality}
              </button>
            ))}
          </div>
        )}
        {!locked && cookiesAccepted && (
          <div className="chat-suggestions" id="routeChips">
            <span className="chip-row-label">Dosage form:</span>
            {ROUTE_CHIPS.map((route) => (
              <button
                key={route}
                type="button"
                className={"chip" + (selectedChips.includes(route) ? " chip-active" : "")}
                onClick={() => toggleChip(route)}
              >
                {route}
              </button>
            ))}
          </div>
        )}
        {!locked && cookiesAccepted && (
          <div className="chat-suggestions" id="phaseChips">
            <span className="chip-row-label">Clinical development stage:</span>
            {PHASE_CHIPS.map((phase) => (
              <button
                key={phase}
                type="button"
                className={"chip" + (selectedChips.includes(phase) ? " chip-active" : "")}
                onClick={() => toggleChip(phase)}
              >
                {phase}
              </button>
            ))}
          </div>
        )}
        <div className="chat-messages" id="chatMessages" ref={messagesRef}>
          {messages.map((m) => (
            <Message
              key={m.id}
              message={m}
              onCreateAccount={goToSignup}
              onBuyPoints={goToBuyPoints}
            />
          ))}
          {isTyping && (
            <div className="msg msg-agent">
              <div className="typing"><span></span><span></span><span></span></div>
            </div>
          )}
        </div>
        <form className="chat-input-row" id="chatForm" onSubmit={handleSubmit}>
          <input
            type="text"
            id="chatInput"
            placeholder={!cookiesAccepted ? "Accept cookies below to chat with the agent" : locked ? lockedPlaceholder : "Ask a CMC regulatory question or describe what you need. Select the above fields. Submit!"}
            autoComplete="off"
            value={inputValue}
            onChange={(e) => { setInputValue(e.target.value); setSelectedChips([]); }}
            disabled={locked || !cookiesAccepted}
          />
          <button type="submit" className="chat-send" aria-label="Send question" disabled={locked || !cookiesAccepted}>
            <svg viewBox="0 0 20 20" fill="none"><path d="M3 10h14M11 4l6 6-6 6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        </form>
        <div className="new-chat-row">
          <button type="button" className="new-chat-btn" onClick={handleNewChat} disabled={isTyping}>
            New chat
          </button>
        </div>
      </div>
      {hasConversation && !isTyping && (
        <div className="report-row">
          <button type="button" className="btn btn-primary" onClick={generateReport}>Generate Report</button>
        </div>
      )}
    </div>
  );
}
